import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSubscriptionStatus } from '../../api/subscription/SubscriptionAPI';
import ROUTER_CONSTANT from '../../constants/RouterConstant';
import { getUserType } from '../../utils/cachedUser';
import Loader from '../common/Loader';
import { Lock } from '../common/Icon';
import { GRADIENTS } from '../../styles/theme';

// Kept local rather than imported from SubscriptionView so the eagerly loaded router
// doesn't pull the whole subscription page into the main bundle.
const INK = '#15172a';
const SOFT = '#5b5f73';
const GOLD_FILL = GRADIENTS.gold;
const FONT = "'DM Sans', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

const FEATURE_COPY: Record<string, string> = {
    FUND_MAINTENANCE: 'Fund & maintenance is available on the Standard and Premium plans.',
};

// Plan features for this session, so hopping between gated pages doesn't refetch and flash
// the loader. null = unknown (older backend or failed call) → nothing is locked.
const CACHE_MS = 60_000;
let cached: { features: string[] | null; at: number } | null = null;

type Access = 'loading' | 'allowed' | 'locked';

const FeatureRoute: React.FC<{ feature: string; children: React.ReactNode }> = ({ feature, children }) => {
    const navigate = useNavigate();
    const role = getUserType();
    const decide = (features: string[] | null): Access => (!features || features.includes(feature) ? 'allowed' : 'locked');
    const fresh = cached && Date.now() - cached.at < CACHE_MS ? cached : null;
    const [access, setAccess] = useState<Access>(() => {
        if (role === 'AGENT') return 'allowed';
        return fresh ? decide(fresh.features) : 'loading';
    });

    useEffect(() => {
        if (access !== 'loading') return;
        let cancelled = false;
        getSubscriptionStatus(navigate).then(res => {
            const features = res.success ? res.data?.features ?? null : null;
            cached = { features, at: Date.now() };
            if (!cancelled) setAccess(decide(features));
        });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [access, navigate, feature]);

    if (access === 'loading') return <Loader loadingText="Loading…" />;
    if (access === 'allowed') return <>{children}</>;

    const isAdmin = role === 'SOCIETY_ADMIN';
    return (
        <div style={{
            minHeight: '100vh', background: '#fff', color: INK, fontFamily: FONT,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 24px',
        }}>
            <div role="alert" style={{ maxWidth: 340, textAlign: 'center' }}>
                <span aria-hidden style={{
                    width: 56, height: 56, borderRadius: 18, margin: '0 auto 16px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: '#fffaf0', color: INK, border: '1px solid #f0e8d0',
                }}><Lock size={24} strokeWidth={2} /></span>
                <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, letterSpacing: '-0.3px' }}>Not in your plan</h1>
                <p style={{ margin: '8px 0 20px', fontSize: 14, lineHeight: 1.5, color: SOFT }}>
                    {FEATURE_COPY[feature] ?? "Your society's plan doesn't include this feature."}
                    {!isAdmin && ' Ask your society admin to upgrade.'}
                </p>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                    <button type="button" onClick={() => navigate(ROUTER_CONSTANT.ROOT.HOME, { replace: true })} style={{
                        padding: '11px 18px', borderRadius: 12, border: '1px solid #e6e7ee', background: '#fff',
                        color: INK, fontSize: 14, fontWeight: 600, cursor: 'pointer',
                    }}>Go home</button>
                    {isAdmin && (
                        <button type="button" onClick={() => navigate(ROUTER_CONSTANT.SOCIETY.SUBSCRIPTION)} style={{
                            padding: '11px 18px', borderRadius: 12, border: 'none', background: GOLD_FILL,
                            color: INK, fontSize: 14, fontWeight: 700, cursor: 'pointer',
                        }}>View plans</button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FeatureRoute;
